<template>
  <teleport :to="appendTo">
    <div :class="kls" v-bind="$attrs">
      <z-tour-mask
        :visible="mergedShowMask"
        :fill="mergedMaskStyle?.color"
        :style="mergedMaskStyle?.style"
        :pos="pos"
        :z-index="mergedZIndex"
        :target-area-clickable="targetAreaClickable"
      />
      <z-tour-content
        v-if="modelValue"
        :key="current"
        :reference="triggerTarget"
        :placement="mergedPlacement"
        :show-arrow="mergedShowArrow"
        :z-index="mergedZIndex"
        :style="mergedContentStyle"
        @close="onEscClose"
      >
        <z-tour-steps :current="current" @update-total="onUpdateTotal">
          <slot />
        </z-tour-steps>
      </z-tour-content>
    </div>
  </teleport>
  <!-- just for IDE -->
  <slot v-if="false" name="indicators" :current="current + 1" :total="total" />
</template>

<script lang="ts" setup>
import { computed, provide, ref, toRef, useSlots, watch } from 'vue'
import { useVModel } from '@vueuse/core'
import { useNamespace, useZIndex } from '@zoryn/hooks'
import { isBoolean } from '@zoryn/utils'
import { CHANGE_EVENT, UPDATE_MODEL_EVENT } from '@zoryn/constants'
import ZTourMask from './mask.vue'
import ZTourContent from './content.vue'
import ZTourSteps from './steps'
import { tourEmits } from './tour'
import { tourKey, useTarget } from './helper'

import type { TourStepProps } from './step'
import type { TourProps } from './tour'

defineOptions({
  name: 'ZTour',
  inheritAttrs: false,
})

const props = withDefaults(defineProps<TourProps>(), {
  current: 0,
  showArrow: true,
  showClose: true,
  placement: 'bottom',
  mask: true,
  gap: () => ({ offset: 6, radius: 2 }),
  scrollIntoViewOptions: () => ({ block: 'center' }),
  appendTo: 'body',
  closeOnPressEscape: true,
  targetAreaClickable: true,
})
const emit = defineEmits(tourEmits)

const ns = useNamespace('tour')
const total = ref(0)
const currentStep = ref<TourStepProps>()

const current = useVModel(props, 'current', emit, {
  passive: true,
})

const currentTarget = computed(() => currentStep.value?.target)

const kls = computed(() => [
  ns.b(),
  mergedType.value === 'primary' ? ns.m('primary') : '',
])

const mergedPlacement = computed(
  () => currentStep.value?.placement || props.placement
)

const mergedContentStyle = computed(
  () => currentStep.value?.contentStyle ?? props.contentStyle
)

const mergedMask = computed(() => currentStep.value?.mask ?? props.mask)
const mergedShowMask = computed(() => !!mergedMask.value && props.modelValue)
const mergedMaskStyle = computed(() =>
  isBoolean(mergedMask.value) ? undefined : mergedMask.value
)

const mergedShowArrow = computed(
  () =>
    !!currentTarget.value && (currentStep.value?.showArrow ?? props.showArrow)
)

const mergedScrollIntoViewOptions = computed(
  () => currentStep.value?.scrollIntoViewOptions ?? props.scrollIntoViewOptions
)
const mergedType = computed(() => currentStep.value?.type ?? props.type)

const { nextZIndex } = useZIndex()
const nowZIndex = nextZIndex()
const mergedZIndex = computed(() => props.zIndex ?? nowZIndex)

const { mergedPosInfo: pos, triggerTarget } = useTarget(
  currentTarget,
  toRef(props, 'modelValue'),
  toRef(props, 'gap'),
  mergedMask,
  mergedScrollIntoViewOptions
)

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      current.value = 0
    }
  }
)

const onEscClose = () => {
  if (props.closeOnPressEscape) {
    emit(UPDATE_MODEL_EVENT, false)
    emit('close', current.value)
  }
}

const onUpdateTotal = (val: number) => {
  total.value = val
}

const slots = useSlots()

provide(tourKey, {
  currentStep,
  current,
  total,
  showClose: toRef(props, 'showClose'),
  closeIcon: toRef(props, 'closeIcon'),
  mergedType,
  ns,
  slots,
  updateModelValue(modelValue) {
    emit(UPDATE_MODEL_EVENT, modelValue)
  },
  onClose() {
    emit('close', current.value)
  },
  onFinish() {
    emit('finish')
  },
  onChange() {
    emit(CHANGE_EVENT, current.value)
  },
})
</script>
